import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Building2, Award } from "lucide-react";

const PROFESSIONAL_PLANS = [
  {
    id: "free-pro",
    name: "Free",
    price: 0,
    period: "forever",
    highlight: false,
    tagline: "Get discovered.",
    features: [
      "Complete profile with photo, bio, expertise",
      "Appear in browse & search",
      "Accept bookings",
      "12% platform fee on each booking",
      "Standard email support",
    ],
    cta: "Get started",
  },
  {
    id: "pro-plus",
    name: "Pro Plus",
    price: 499,
    period: "month",
    highlight: true,
    tagline: "Get discovered faster.",
    features: [
      "Everything in Free",
      "Priority placement in browse results",
      "Featured in AI Match recommendations",
      "'Pro Plus' badge on your profile",
      "Reduced platform fee: 10% (vs 12%)",
      "Detailed analytics — profile views, conversion",
      "Priority verification (usually within 4 hours)",
    ],
    cta: "Upgrade to Pro Plus",
  },
];

const CLIENT_PLANS = [
  {
    id: "pay-as-go",
    name: "Pay-as-you-go",
    price: 0,
    period: "forever",
    highlight: false,
    tagline: "No commitment.",
    features: [
      "Browse all experts",
      "Free AI Match (3 recommendations per query)",
      "Pay per session only",
      "Standard booking flow",
    ],
    cta: "Start browsing",
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: 999,
    period: "month",
    highlight: true,
    tagline: "Learn without limits.",
    features: [
      "Unlimited AI Matches per month",
      "10% discount on all bookings",
      "Faster expert response guaranteed (within 6 hours)",
      "Dedicated account manager",
      "Save & organise favourite experts",
      "Group bookings for your team",
    ],
    cta: "Get Unlimited",
  },
];

function PlanCard({ plan, section }) {
  return (
    <div className={`section-card relative ${plan.highlight ? "border-[#2DD4BF]" : ""}`}>
      {plan.highlight && (
        <div className="absolute -top-3 left-8 bg-[#2DD4BF] text-[#05070C] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
          Most Popular
        </div>
      )}
      <div className="label-eyebrow">{section}</div>
      <div className="font-display text-3xl text-[#E8ECF3] mt-3">{plan.name}</div>
      <p className="text-[#94A3B8] text-sm mt-1">{plan.tagline}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display text-5xl text-[#E8ECF3]">₹{plan.price}</span>
        <span className="text-[#94A3B8]">/{plan.period}</span>
      </div>
      <ul className="mt-8 space-y-3">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#CBD5E1]">
            <Check className="h-4 w-4 text-[#2DD4BF] mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <Link to="/register" data-testid={`plan-${plan.id}`}>
          <button className={plan.highlight ? "pill-solid w-full h-11 font-medium" : "pill-glow w-full h-11 font-medium"}>
            {plan.cta}
          </button>
        </Link>
      </div>
    </div>
  );
}

export default function PricingPage() {
  return (
    <div className="bg-[#05070C] min-h-[80vh]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 text-center relative">
          <div className="label-eyebrow">Pricing</div>
          <h1 className="font-display text-5xl md:text-7xl mt-4 leading-tight">
            Fair plans for <span className="serif-italic text-[#2DD4BF]">every side</span>.
          </h1>
          <p className="text-[#94A3B8] max-w-2xl mx-auto mt-6 text-lg">
            Free to join. Simple upgrades when you want to grow. No hidden charges, no surprises.
          </p>
        </div>
      </section>

      {/* Expert plans */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Award className="h-6 w-6 text-[#2DD4BF]" />
          <div className="font-display text-3xl text-[#E8ECF3]">For Retired Professionals</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {PROFESSIONAL_PLANS.map((p) => <PlanCard key={p.id} plan={p} section="Expert" />)}
        </div>

        {/* Rate note */}
        <div className="mt-10 section-card border-[#2DD4BF]/40 bg-[#2DD4BF]/5">
          <div className="flex items-start gap-4">
            <div className="teal-icon-circle" style={{ width: 48, height: 48 }}>₹</div>
            <div className="flex-1">
              <div className="font-display text-xl text-[#E8ECF3]">You set your own rate</div>
              <p className="text-[#CBD5E1] mt-2 text-sm leading-relaxed">
                Every expert decides their own hourly rate — <strong className="text-[#2DD4BF]">₹800, ₹1,200, ₹2,500, ₹5,000</strong> — whatever reflects your experience. 2nd Innings never sets your price. We only take a small platform fee (12% Free plan, 10% Pro Plus) — the rest is 100% yours.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#0A1223] rounded-xl p-3">
                  <div className="text-xs text-[#94A3B8]">If you charge ₹1,000/hr</div>
                  <div className="font-display text-2xl text-[#2DD4BF] mt-1">₹880</div>
                  <div className="text-[10px] text-[#94A3B8]">you earn per hour</div>
                </div>
                <div className="bg-[#0A1223] rounded-xl p-3">
                  <div className="text-xs text-[#94A3B8]">If you charge ₹2,500/hr</div>
                  <div className="font-display text-2xl text-[#2DD4BF] mt-1">₹2,200</div>
                  <div className="text-[10px] text-[#94A3B8]">you earn per hour</div>
                </div>
                <div className="bg-[#0A1223] rounded-xl p-3">
                  <div className="text-xs text-[#94A3B8]">If you charge ₹5,000/hr</div>
                  <div className="font-display text-2xl text-[#2DD4BF] mt-1">₹4,400</div>
                  <div className="text-[10px] text-[#94A3B8]">you earn per hour</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client plans */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Sparkles className="h-6 w-6 text-[#2DD4BF]" />
          <div className="font-display text-3xl text-[#E8ECF3]">For Clients</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {CLIENT_PLANS.map((p) => <PlanCard key={p.id} plan={p} section="Client" />)}
        </div>
      </section>

      {/* Enterprise */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24">
        <div className="section-card bg-[#0A1223] border-[#142542] p-10 md:p-16 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="h-6 w-6 text-[#2DD4BF]" />
              <div className="label-eyebrow">Enterprise</div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#E8ECF3] leading-tight">
              Building teams that need <span className="serif-italic text-[#2DD4BF]">multiple experts</span>?
            </h2>
            <p className="text-[#94A3B8] mt-4 max-w-xl">
              Corporate partnerships for startups, SMBs and CSR programs — bulk booking rates, curated expert pools, and quarterly advisory boards. Custom pricing.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/contact">
              <button className="pill-solid h-12 px-8 font-medium">Contact Sales</button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ note */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-24 text-center">
        <p className="text-[#94A3B8]">
          Got questions?{" "}
          <Link to="/faq" className="text-[#2DD4BF] hover:underline">Read the FAQs</Link>{" "}
          or{" "}
          <Link to="/contact" className="text-[#2DD4BF] hover:underline">contact us</Link>.
        </p>
      </section>
    </div>
  );
}
